import { useState } from "react";

import { cn } from "../../lib/cn";
import { InputGroupInput } from "../../primitive/input-group";

import { getFieldError } from "./get-field-error";
import { useFieldContext } from "./form";

interface FormInputGroupInputProps extends Omit<
  React.ComponentProps<typeof InputGroupInput>,
  "value" | "onChange" | "name"
> {
  showErrorMessage?: boolean;
}

function FormInputGroupInput({
  showErrorMessage = true,
  type,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  className,
  ...props
}: FormInputGroupInputProps) {
  const field = useFieldContext<string | number>();
  const [localValue, setLocalValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    if (type === "number") {
      setLocalValue(nextValue);
      const parsedValue = Number(nextValue);
      field.handleChange(
        nextValue === "" || Number.isNaN(parsedValue) ? 0 : parsedValue,
      );
      return;
    }

    field.handleChange(nextValue);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (type === "number") {
      setLocalValue(String(field.state.value ?? ""));
      setIsFocused(true);
    }

    onFocusProp?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (type === "number") {
      const parsedValue = Number(localValue);
      const finalValue = Number.isInteger(parsedValue) ? parsedValue : 0;
      field.handleChange(finalValue);
      field.handleBlur();
      setIsFocused(false);
    }

    onBlurProp?.(event);
  };

  const displayValue =
    type === "number"
      ? isFocused
        ? localValue
        : String(field.state.value ?? "")
      : (field.state.value ?? "");

  return (
    <>
      <InputGroupInput
        id={field.name}
        name={field.name}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        type={type}
        className={cn(
          type === "number" &&
            "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          className,
        )}
        {...props}
      />
      {showErrorMessage && field.state.meta.errors.length > 0 ? (
        <span className="text-sm text-destructive">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      ) : null}
    </>
  );
}

export { FormInputGroupInput };
